import { motion, useScroll, useTransform, Variants } from 'framer-motion';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getBookingType, getTableDisplayName, requiresRequestFlow, TDR } from 'tdr-common';
import { useSearch } from '../../api/availability/search';
import { useTableWithPolicies } from '../../api/getTable';
import { useTableAnnouncement } from '../../api/getTableAnnouncement';
import { useTimeslot } from '../../api/getTimeslot';
import { useViewToggle } from '../../context/ViewToggleProvider';
import { useScreenSize } from '../../hooks/useScreenSize';
import { SearchFilters, serializeSearchFiltersForURL } from '../../routes/AvailabilityGuard';
import { FindAvailabilityButton } from '../buttons/FindAvailabilityButton';
import MediaCarousel from '../media-carousel/MediaCarousel';
import ScrollToTop from '../scroll-to-top/scroll-to-top';
import { Skeleton } from '../skeleton/Skeleton';
import { TableDrawerTags } from './TableDrawerTags';
import { analytics, RESULT_VIEWED } from '../../helpers/analytics';
import { useGTMAnalytics } from '../../helpers/gtm/surface/useGTMAnalytics';
import BackButton from '../buttons/BackButton';
import { TableDrawerHeader } from './TableDrawerHeader';
import { SidebarFiltersHeader } from '../sidebar-filters-header/SidebarFiltersHeader';
import { BookNowFooter } from '../search/BookNowFooter';
import { useOutletContext } from 'react-router-dom';
import { AvailabilityGuardContext } from '../../routes/AvailabilityGuard';
import { useEstimateInvoice } from '../../hooks/useEstimateInvoice';
import { useFeatureFlags } from '../../api/getRestaurantFeatureFlags';
import { PriceDisplay, PriceDisplayVariant } from '../__design-system/price-display/PriceDisplay';
import { useEventsByRestaurant } from '../../api/getEventsByRestaurant';
import { EventBookingBanner } from './EventBookingBanner';
import PolicySection from '../policies/PolicySection';
import { useTranslation } from 'react-i18next';
import { resolveLocalizedText } from '@/helpers/resolveLocalizedText';
import { getMatchingEventsForTable } from '../../helpers/matchingEvents';
import { getSearchResult } from '../inventory-carousel/categorizeTables';
const MEDIA_HEIGHT = 382;
const MEDIA_WIDTH = 415;

export type BookingFlowVariant = 'default' | 'modification';

type TableDrawerProps = {
	selectedTable: TDR.Table;
	restaurant: TDR.Restaurant;
	searchFilters: SearchFilters;
};

const TableDrawer = ({ selectedTable, restaurant, searchFilters }: TableDrawerProps) => {
	const { t, i18n } = useTranslation();
	const gtm = useGTMAnalytics();
	const { data: tableWithPolicies } = useTableWithPolicies({
		id: selectedTable.id,
		searchParams: searchFilters,
		location: 'table'
	});

	const { results, isLoading } = useSearch({
		params: searchFilters,
		restaurantId: restaurant.id,
		space: [selectedTable]
	});

	const { data: localAnnouncement } = useTableAnnouncement(selectedTable.id);

	const { data } = useTimeslot(selectedTable?.id, searchFilters.date, restaurant.id, searchFilters.time, searchFilters.groupSize);
	const { isSmallScreen } = useScreenSize();
	const { isVirtualView } = useViewToggle();

	const { previousTime, previousDate } = useOutletContext<AvailabilityGuardContext>();

	const scrollContentRef = useRef<HTMLDivElement>(null);
	const hasTrackedView = useRef(false);

	const timeslot = data?.timeSlot;
	const displayUnit = timeslot?.isPerGuest ? t('labels.guest', { defaultValue: 'guest' }) : `${selectedTable.label ?? t('labels.table', { defaultValue: 'table' })}`;
	const [isMounted, setIsMounted] = useState(false);

	const { data: featureFlags } = useFeatureFlags(restaurant.id);
	const { data: restaurantEvents } = useEventsByRestaurant(restaurant.id);
	const { estimatedInvoice } = useEstimateInvoice(restaurant, selectedTable, searchFilters);

	const matchingBookedEvents = useMemo(
		() => getMatchingEventsForTable(restaurantEvents, searchFilters, selectedTable.slug, restaurant.timezone),
		[restaurantEvents, searchFilters, selectedTable.slug, restaurant.timezone]
	);

	const coverImagePath = selectedTable?.carouselImagePath;
	let mediaItems = selectedTable?.media || [];

	if (isSmallScreen || mediaItems.length === 0) {
		mediaItems = [{ type: TDR.MediaType.IMAGE, path: coverImagePath } as TDR.MediaContent, ...mediaItems];
	}
	else {
		mediaItems = mediaItems.length > 0 ? mediaItems : [{ type: TDR.MediaType.IMAGE, path: coverImagePath } as TDR.MediaContent];
	}

	const displayTableName = getTableDisplayName(selectedTable);


	// Resolved for these searchFilters' date and groupSize; undefined until both are known, in which
	// case fall back to the table's own default (`getBookingType` reads the doc, so this stays
	// correct for a table that carries a bookingType of its own).
	const resolvedBookingType = getSearchResult(results?.resultMap, selectedTable?.id)?.bookingType;
	const effectiveBookingType = resolvedBookingType ?? getBookingType(selectedTable);

	const isTableAvailable = results?.resultMap?.[selectedTable?.id]?.match?.length === 3;
	const conflictingFilter = results?.resultMap?.[selectedTable.id]?.conflict?.[0] || undefined;

	const conflictingFilterDisplayMap = {
		date: t('labels.date'),
		time: t('labels.time'),
		groupSize: t('labels.group_size', { defaultValue: 'group size' })
	} as const;

	const mediaVariants: Variants = {
		visible: {
			height: `${MEDIA_HEIGHT}px`,
			minHeight: `${MEDIA_HEIGHT}px`,
			transition: {
				delay: 0.4,
				duration: 0.3
			}
		},
		hidden: {
			height: '100vh',
			transition: {
				duration: 0.5
			}
		}
	};

	const slideAndFade: Variants = {
		visible: {
			opacity: 1,
			transition: {
				duration: 0.3
			}
		},
		hidden: {
			opacity: 0,
			transition: {
				duration: 0.3
			}
		}
	};

	const containerVariants: Variants = {
		visible: {
			opacity: 1
		},
		hidden: {
			opacity: 0
		}
	};

	const { scrollY: scrollYMobile } = useScroll();

	const { scrollY: scrollYDesktop } = useScroll({
		container: scrollContentRef
	});

	const scrollY = isSmallScreen ? scrollYMobile : scrollYDesktop;

	const mediaGalleryOpacity = useTransform(scrollY, [200, MEDIA_HEIGHT], [1, 0]);

	// When in virtual view (mobile), make sure we scroll to top so full matterport iframe is in view
	useEffect(() => {
		if (isVirtualView) {
			window.scrollTo(0, 0);
		}
	}, [isVirtualView]);


	// For determining initial animation states of <motion /> elements
	//  - if component has not mounted yet, set initial to 'visible' to cancel animations
	//     (these animations are only used within the already mounted component, depending on isVirtualView state)
	useEffect(() => {
		setIsMounted(true);
	}, []);

	// Track Result Viewed event once per page load when component mounts and data is available
	useEffect(() => {
		if (!hasTrackedView.current &&
			selectedTable &&
			tableWithPolicies) {
			const imageCount = mediaItems.filter(item => item.type === TDR.MediaType.IMAGE).length;
			const videoCount = mediaItems.filter(item => item.type === TDR.MediaType.VIDEO).length;
			const descriptionLength = selectedTable.description?.length || 0;

			// Extract policy information
			const policies = tableWithPolicies?.policies || [];
			const policyTypes = policies.map(policy => policy.type);

			// Get table attributes/tags
			const tableAttributes = selectedTable.attributes || {};
			const tags = Object.keys(tableAttributes).filter(key => tableAttributes[key] === true);

			analytics.track(RESULT_VIEWED, {
				restaurantId: restaurant.id,
				restaurantName: restaurant.name,
				tableId: selectedTable.id,
				tableName: selectedTable.name,
				tableType: selectedTable.label || '',
				isLargeGroup: requiresRequestFlow({ bookingType: effectiveBookingType }),
				isPatio: selectedTable.isPatio || false,
				groupSize: searchFilters?.groupSize,
				date: searchFilters?.date,
				time: searchFilters?.time,
				tableHasAnnouncement: !!localAnnouncement?.message,
				announcementCategory: localAnnouncement?.category || '',
				ctaType: 'simplified',
				priceCurrency: restaurant?.currency,
				descriptionLength,
				imageCount,
				videoCount,
				tags,
				policyCount: policies?.length || 0,
				policyTypes,
				isAvailable: isTableAvailable,
				conflictingFilter
			});

			const price = estimatedInvoice?.total ?? estimatedInvoice?.subtotal;
			gtm.viewItem({
				itemId: selectedTable.id,
				itemName: selectedTable.name,
				itemCategory: 'Reservation',
				...(price !== undefined && price !== null && { price })
			});

			// Mark that we've tracked the view
			hasTrackedView.current = true;
		}
	}, [selectedTable, tableWithPolicies, searchFilters, restaurant, localAnnouncement, timeslot, displayUnit, isTableAvailable, conflictingFilter, mediaItems, estimatedInvoice]);

	const getUpdatedSearchParams = () => {
		const updates: Partial<SearchFilters> = {};
		if (previousDate !== '' && searchFilters?.date !== previousDate) {
			updates.date = previousDate;
		}
		if (previousTime !== '' && searchFilters?.time !== previousTime) {
			updates.time = previousTime;
		}
		if (Object.keys(updates).length > 0) {
			return serializeSearchFiltersForURL({ ...searchFilters, ...updates }).toString();
		}
		return serializeSearchFiltersForURL(searchFilters).toString();
	};

	return (
		<motion.article
			className="relative bg-tablz-dark-gray-1 h-full sm:w-[400px] flex flex-col"
			variants={containerVariants}
			initial='visible'
			animate='visible'
			exit={'hidden'}
			transition={{ duration: 0.2 }}
		>
			{/* make sure we always land here scrolled to top */}
			<ScrollToTop />

			<div className="absolute top-2 left-2 z-10">
				<BackButton
					to={{
						pathname: '..',
						search: getUpdatedSearchParams()
					}}
				/>
			</div>

			<div className="h-full overflow-y-scroll" ref={scrollContentRef}>
				<motion.div
					initial={isMounted ? 'hidden' : 'visible'}
					style={{ opacity: mediaGalleryOpacity }}
					animate={isVirtualView ? 'hidden' : 'visible'}
					variants={mediaVariants}
				>
					{!coverImagePath ? (
						<Skeleton height='270px' width='100%' />
					) : (
						<MediaCarousel
							variant={'default'}
							tableLabel={selectedTable.label}
							itemHeight={MEDIA_HEIGHT}
							itemWidth={MEDIA_WIDTH}
							items={mediaItems}
							brightness={restaurant.brightness}
							contrast={restaurant.contrast}
						/>
					)}
				</motion.div>

				<motion.div
					variants={slideAndFade}
					animate={isVirtualView ? 'hidden' : 'visible'}
					className="px-2 py-3 flex flex-col gap-2"
				>
					<TableDrawerHeader
						table={selectedTable}
						tableName={displayTableName}
						priceElement={
							<PriceDisplay
								restaurant={restaurant}
								table={selectedTable}
								searchFilters={searchFilters}
								estimatedInvoice={estimatedInvoice}
								timeslot={timeslot}
								featureFlags={featureFlags}
								variant={PriceDisplayVariant.TABLE_DRAWER}
							/>
						}
					/>
					<div className="px-2 font-poppins text-base text-zinc-300 pb-3" tabIndex={0}>
						{resolveLocalizedText(selectedTable?.description, i18n.language)}
					</div>
					<TableDrawerTags table={selectedTable} />

					{tableWithPolicies?.policies?.length > 0 ? (
						<div className="" tabIndex={0}>
							<PolicySection table={tableWithPolicies} />
						</div>
					) : null}
				</motion.div>
			</div>

			<motion.footer
				className="w-full"
				initial={isMounted ? 'hidden' : 'visible'}
				animate={isVirtualView ? 'hidden' : 'visible'}
				variants={slideAndFade}
			>
				{isLoading ? (
					<Skeleton width='100%' height='56px' />
				) : isTableAvailable ? (
					<div className="bg-tablz-dark-gray-2 border-t border-tablz-med-gray-4 pt-2 px-2">
						<EventBookingBanner matchingEvents={matchingBookedEvents} />
						<SidebarFiltersHeader />

						<div className="p-2 cursor-pointer w-full">
							<BookNowFooter
								displayUnit={displayUnit}
								bookingType={resolvedBookingType}
							/>
						</div>
					</div>
				) : (
					<FindAvailabilityButton
						label={conflictingFilter ? t('actions.change_x', { x: conflictingFilterDisplayMap[conflictingFilter], defaultValue: `Change ${conflictingFilterDisplayMap[conflictingFilter]}` }) : t('titles.find_availability')}
					/>
				)}
			</motion.footer>
		</motion.article>
	);
};

export default TableDrawer;
